import React, { useContext, useEffect, useState } from 'react';
import { Button, Empty, Modal, Tooltip } from '@maxtropy/components';
import { Space } from 'antd';
import { useRequest } from 'ahooks';
import { V2GasMonitorConfigDetailPostResponse } from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';
import outerStyles from '../index.module.scss';
import { GasMonitorContext } from '../../../context';
import { EnergyFlowChartNodeItem, getEnergyFlowChart } from '@/api/plustekSankey';
import { isNil } from 'lodash-es';
import FlowEcharts from './FlowEcharts';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';
import { DoubleRightOutlined } from '@ant-design/icons';
import qs from 'qs';
import { formatTime, RadioType } from '@/pages/PlustekSankey/shared';
import dayjs, { Dayjs } from 'dayjs';

interface EnergyFlowModalProps {
  open: boolean;
  onClose: () => void;
  configDetail?: V2GasMonitorConfigDetailPostResponse;
}

const filterSankeyData = (allNodes: EnergyFlowChartNodeItem[], fromLevel: number, toLevel: number) => {
  const nodes = [...allNodes].filter(i => i.depth + 1 >= fromLevel && i.depth + 1 <= toLevel);
  // 3. 重新映射 level，从 0 开始
  const minLevel = Math.min(...nodes.map(n => n.depth));
  return nodes.map(n => ({
    ...n,
    depth: n.depth - minLevel, // 重新映射
  }));
};
type RangeValue = [Dayjs | null, Dayjs | null] | null;

const EnergyFlowModal: React.FC<EnergyFlowModalProps> = ({ open, onClose, configDetail }) => {
  const { isFullScreen, isStandardSize, scale } = useContext(ShareDataWrapperContext);
  const { timeResolution } = useContext(GasMonitorContext);
  const [onlyKey, setOnlyKey] = useState<number>();
  const [flowTimeResolution] = useState<RadioType>(RadioType.DAY);
  const [value] = useState<RangeValue>([dayjs().startOf('month'), dayjs()]);

  const [rangeTime, setRangeTime] = useState<RangeValue>();

  // 用能趋势
  const { data: flowChartData, loading: flowChartLoading } = useRequest(
    async () => {
      const t = formatTime(rangeTime as any, flowTimeResolution);

      const res = await getEnergyFlowChart({
        ...t,
        energyUnitGroupId: onlyKey!,
      });

      let chartDataTemp = {
        nodes: (res.nodes ?? [])
          .map(item => ({
            ...item,
            name: item.key,
          }))
          .filter(c => !isNil(c.value) && c.value !== 0),
        links: res.links.filter(item => !isNil(item.value) && item.value !== 0),
      };
      const fromLevel = configDetail?.energyUnitGroupReqs?.find(item => item.energyGroupId === onlyKey)?.fromLevel;
      const toLevel = configDetail?.energyUnitGroupReqs?.find(item => item.energyGroupId === onlyKey)?.toLevel;
      if (fromLevel && toLevel) {
        return {
          nodes: filterSankeyData(chartDataTemp.nodes, fromLevel, toLevel),
          links: chartDataTemp.links,
        };
      }
    },
    {
      ready: !!open && !!onlyKey && !!rangeTime && !!timeResolution,
      refreshDeps: [open, onlyKey, rangeTime, timeResolution],
    }
  );

  // 跟能源概览保持一致
  useEffect(() => {
    if (!flowTimeResolution) return;
    if (flowTimeResolution === RadioType.DAY) {
      setRangeTime([value?.[0]?.startOf('D') ?? null, value?.[1]?.endOf('D') ?? null]);
    } else {
      setRangeTime([value?.[0]?.startOf('month') ?? null, value?.[1] ?? null]);
    }
  }, [value, flowTimeResolution]);
  // useEffect(() => {
  //   if (!timeResolution || isEmpty(dateValue)) return;
  //   if (timeResolution === IndexTimeActive.THIS_MONTH) {
  //     setFlowTimeResolution(RadioType.DAY);
  //     setRangeTime([dateValue?.startOf('D') ?? null, dateValue?.endOf('D') ?? null]);
  //   } else {
  //     setFlowTimeResolution(RadioType.MONTH);
  //     setRangeTime([dateValue?.startOf('month') ?? null, dateValue?.endOf('month') ?? null]);
  //   }
  // }, [timeResolution, dateValue]);

  // 默认选择一个用能分析组
  useEffect(() => {
    if (!configDetail) return;
    setOnlyKey(configDetail?.energyUnitGroupReqs?.[0]?.energyGroupId!);
  }, [configDetail]);

  return (
    <Modal
      title={
        <div>
          用气能流
          <Button
            type="link"
            onClick={() => {
              window.open(
                `/energy/analysis/unitSankey?${qs.stringify(
                  {
                    radioType: flowTimeResolution,
                    fromTime: formatTime(rangeTime as any, flowTimeResolution).fromTime,
                    toTime: formatTime(rangeTime as any, flowTimeResolution).toTime,
                    unitGroupId: onlyKey,
                  },
                  { indices: false }
                )} `,
                '_blank'
              );
            }}
          >
            详情 <DoubleRightOutlined className="translationStyles" />
          </Button>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      {...(isStandardSize ? { size: 'big' as const } : { width: 1900 })}
      destroyOnClose
      className={outerStyles.customModal}
      centered
      style={{ top: isFullScreen ? 80 * (scale ?? 1) : 110 * (scale ?? 1) }}
      getContainer={() => document.getElementById('full_screen_box') ?? document.body}
    >
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <Space size={8} style={{ flexWrap: 'wrap' }}>
            {(configDetail?.energyUnitGroupReqs ?? []).map((item, index) => (
              <div
                key={index}
                className={`${styles.dateBtn} ${onlyKey === item.energyGroupId && styles.active}`}
                onClick={() => {
                  setOnlyKey(item.energyGroupId);
                }}
              >
                <Tooltip title={item.energyGroupName}>{item.energyGroupName}</Tooltip>
              </div>
            ))}
          </Space>
        </div>
        <div className={styles.content}>
          {flowChartData ? (
            <FlowEcharts
              flowChartLoading={flowChartLoading}
              isMedium={false}
              data={flowChartData}
              // physicalUnitName={}
            />
          ) : (
            <Empty style={{ marginTop: 200 }} />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EnergyFlowModal;
